<script>
  import { createEventDispatcher, onMount } from "svelte"
  import notify from "../../Script/notify"
  import { loadImage } from "../../Pages/Publish/BulkUpload/Script/bulkUpload"

  const dispatch = createEventDispatcher()

  export let blobURL

  export let enableDarkMode = true

  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  let closeNotification = {
    close() {},
  }

  /** @type {HTMLCanvasElement} */
  let canvas
  /**
   * @type {CanvasRenderingContext2D}
   */
  let ctx

  let imageForProcessing

  onMount(async () => {
    try {
      imageForProcessing = await loadImage(blobURL)

      //   setTimeout(async () => {
      ctx = canvas.getContext("2d", { willReadFrequently: true })
      await applyGrayScale(150)
      //   }, 10)
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "Badge Publish (Background Removal)\nEVENT: onMount"
      closeNotification = notify.danger(error, info)
    }
  })

  const applyGrayScale = (intensity) => {
    return new Promise(async (resolve, reject) => {
      try {
        // intensity = 255 - intensity

        // console.log("intensity", intensity);
        intensity = Number(intensity)

        const width = imageForProcessing.naturalWidth
        const height = imageForProcessing.naturalHeight

        canvas.width = width
        canvas.height = height
        ctx.drawImage(imageForProcessing, 0, 0, width, height)

        const imageData = ctx.getImageData(0, 0, width, height)
        const data = imageData.data

        for (let i = 0; i < data.length; i += 4) {
          const avg = Math.floor(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2])
          // const avg = Math.floor((data[i] + data[i + 1] + data[i + 2]) / 3)

          if (intensity < avg) {
            data[i] = 0
            data[i + 1] = 0
            data[i + 2] = 0
            data[i + 3] = 0
          }
        }

        ctx.putImageData(imageData, 0, 0)

        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }

  const handleGrayScaleSliding = async (e) => {
    try {
      await applyGrayScale(e.target.value)
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "Badge Publish (Background Removal)\nEVENT: While removing background based on slide input"
      closeNotification = notify.danger(error, info)
    }
  }

  const handleEnhancedImage = async () => {
    try {
      let blobObject = await canvasToBlob()

      dispatch("removeBg", blobObject)
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "Badge Publish (Background Removal)\nEVENT: While confirming to proceed with removed background"
      closeNotification = notify.danger(error, info)
    }
  }

  /**
   * Converts a canvas element to a Blob object.
   *
   * @returns {Promise<Blob>} A Promise that resolves with the generated Blob object.
   */
  const canvasToBlob = () => {
    /**
     * @typedef {Blob} Blob - Represents a binary large object (Blob) that can store binary data.
     * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Blob Blob}
     */

    return new Promise((resolve) => {
      /**
       * The `toBlob` method of the canvas element is used to convert it to a Blob.
       * @param {Blob} blob - The generated Blob object.
       */
      canvas.toBlob((blob) => {
        // Once the Blob is created, the Promise is resolved with the Blob object.
        resolve(blob)
      }, "image/png")
    })
  }
</script>

<div class="fixed inset-0 z-20 !mt-0 flex items-center justify-center overflow-hidden bg-[#000000cc] backdrop-blur-md">
  <form on:submit|preventDefault={handleEnhancedImage} class="relative m-6 flex w-[700px] transform select-none flex-col items-center rounded-md {enableDarkMode ? 'bg-white dark:bg-bodyblack' : 'bg-bodyblack'} transition-all md:m-0">
    <div class="flex w-full items-center justify-center rounded-md p-3 md:p-6">
      <h1 class="text-xl font-bold lg:text-2xl xl:text-2xl {enableDarkMode ? 'text-black dark:text-white' : 'text-white'}">Remove Background</h1>
    </div>

    <div class="relative flex h-full w-full items-center rounded-md p-3">
      <div class="flex h-full w-full flex-col items-center gap-3">
        <canvas bind:this={canvas} class="aspect-auto max-h-[40vh] max-w-[75%] rounded-md border border-gray-400" style="background:url('assets/img/grid.png')" />
        <div class="w-1/2">
          <input on:input={handleGrayScaleSliding} type="range" min="100" max="230" value="150" class="cursor-pointer {enableDarkMode ? '!bg-[#e0e0e0] dark:!bg-[#484849]' : 'bg-[#484849]'}" />
        </div>
      </div>
    </div>

    <div class="flex flex-row items-center justify-center gap-3 rounded-md p-2 sm:flex md:gap-0 md:p-4">
      <button on:click type="button" class="flex h-9 min-h-full min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] {enableDarkMode ? 'bg-[#f7f7f7] text-gray-900 dark:bg-lightgray dark:text-white' : 'bg-lightgray text-white'} px-3 py-1.5 text-sm font-medium hover:bg-red-500 hover:text-white focus:outline-none hover:dark:bg-red-500 sm:ml-3 sm:w-auto">
        <span>
          <i class="fa-solid fa-circle-xmark" />
        </span>
        <span class="select-none">&nbsp;&nbsp;Cancel&nbsp;&nbsp;</span>
      </button>
      <button on:click={handleEnhancedImage} type="submit" class="flex h-9 min-h-full min-w-[36px] items-center justify-center rounded-md border border-[#a0a1a580] {enableDarkMode ? 'bg-[#f7f7f7] text-gray-900 dark:bg-lightgray dark:text-white' : 'bg-lightgray text-white'} px-3 py-1.5 text-sm font-medium hover:bg-green-500 hover:text-white focus:outline-none hover:dark:bg-green-500 sm:ml-3 sm:w-auto">
        <span>
          <i class="fa-solid fa-circle-check" />
        </span>
        <span class="select-none">&nbsp;&nbsp;Confirm&nbsp;&nbsp;</span>
      </button>
    </div>
  </form>
</div>
